import { InjectionToken } from '@angular/core';

/**
 * Supported portal brands. The active brand is derived from the application host
 * (see {@link getBrand}) so a single build can be served under multiple brands.
 */
export enum Brand {
  Salt = 'salt',
  RocketKor = 'rocketkor',
}

export interface BrandConfig {
  brand: Brand;
  /** Human-readable brand name (page title, brand labels). */
  displayName: string;
  /** Legal company name used in the footer copyright. */
  companyName: string;
  /** Wide wordmark logo (auth page). */
  logo: string;
  /** Compact square icon (sidebar, error/maintenance/search screens). */
  logoIcon: string;
  logoAlt: string;
  /** Path to the favicon asset. */
  favicon: string;
}

export const BRAND_CONFIGS: Record<Brand, BrandConfig> = {
  [Brand.RocketKor]: {
    brand: Brand.RocketKor,
    displayName: 'RocketKOR Admin Portal',
    companyName: 'Rocket Financial, Inc.',
    logo: './assets/img/rocketkor-logo-admin.svg',
    logoIcon: './assets/img/rocket-icon.svg',
    logoAlt: 'RocketKOR Admin Portal',
    favicon: './assets/img/rocket.svg',
  },
  [Brand.Salt]: {
    brand: Brand.Salt,
    displayName: 'Salt Treasury Operations',
    companyName: 'Salt Treasury, Inc.',
    logo: './assets/img/salt-logo-white.png',
    logoIcon: './assets/img/salt-logo-dark.png',
    logoAlt: 'Salt Operations',
    favicon: './assets/img/salt-favicon.ico',
  },
};

// TEMP: force a brand during local development so SALT branding can be previewed
// without a "salt" host (which the QA backend rejects via CORS). Set back to `null`
// to restore pure host-based detection locally. Deployed hosts are unaffected.
const LOCAL_DEV_BRAND: Brand | null = Brand.Salt;

const isLocalHost = (host: string): boolean => host.startsWith('localhost') || host.startsWith('127.0.0.1');

/**
 * Resolves the active brand from the host. A host containing the word "salt"
 * activates SALT branding; everything else (including "rocketkor" hosts and the
 * local default) falls back to RocketKOR.
 */
export const getBrand = (host: string = window.location.host): Brand => {
  if (LOCAL_DEV_BRAND && isLocalHost(host)) {
    return LOCAL_DEV_BRAND;
  }

  return host.toLowerCase().includes('salt') ? Brand.Salt : Brand.RocketKor;
};

export const getBrandConfig = (host?: string): BrandConfig => BRAND_CONFIGS[getBrand(host)];

// Root-provided with a default factory so the token is always resolvable — in the
// app and in any TestBed — without each injector needing to register it explicitly.
export const BRAND_CONFIG = new InjectionToken<BrandConfig>('Brand Config', {
  providedIn: 'root',
  factory: () => getBrandConfig(),
});
